import React from 'react';
import { Star } from 'lucide-react';
import { StarRating } from '@/components/StarRating';
import type { BookReviewStats as Stats } from '@/hooks/useBookReviews';

interface BookReviewStatsProps {
  stats: Stats;
}

export function BookReviewStats({ stats }: BookReviewStatsProps) {
  return (
    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold">{stats.averageRating.toFixed(1)}</span>
            <Star className="w-6 h-6 fill-primary text-primary mb-1" />
          </div>
          <div className="text-sm text-muted-foreground">
            <div>out of 5</div>
            <div>{stats.totalRatings} {stats.totalRatings === 1 ? 'rating' : 'ratings'}</div>
          </div>
        </div>

        {/* Visual star representation */}
        <div className="hidden sm:block">
          <StarRating value={stats.averageRating} readonly size="md" />
        </div>
      </div>

      {/* Rating distribution bars (optional - can be hidden if space is limited) */}
      <div className="space-y-1 pt-2 border-t border-border/50">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.ratingDistribution[rating] || 0;
          const percentage = stats.totalRatings > 0 ? (count / stats.totalRatings) * 100 : 0;
          
          return (
            <div key={rating} className="flex items-center gap-2 text-xs">
              <span className="w-6 text-muted-foreground">{rating}★</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-8 text-right text-muted-foreground">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
